// src/store/slices/admin/testimonialSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

// ===== THUNKS =====
export const getTestimonials = createAsyncThunk(
    'testimonials/getTestimonials',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.TESTIMONIALS, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Testimonials retrieved', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch testimonials', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch testimonials';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const getTestimonial = createAsyncThunk(
    'testimonials/getTestimonial',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.TESTIMONIAL(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Testimonial retrieved', testimonial: data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch testimonial', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch testimonial';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const createTestimonial = createAsyncThunk(
    'testimonials/createTestimonial',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.post(API_ENDPOINTS.TESTIMONIALS + '/create', data);
            const { status, message, data: responseData } = response.data;
            if (status === 201 || status === 200) {
                return { success: true, message: message || 'Testimonial created', testimonial: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to create testimonial', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to create testimonial';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const updateTestimonial = createAsyncThunk(
    'testimonials/updateTestimonial',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.put(API_ENDPOINTS.TESTIMONIAL(id), data);
            const { status, message, data: responseData } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Testimonial updated', testimonial: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to update testimonial', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to update testimonial';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const deleteTestimonial = createAsyncThunk(
    'testimonials/deleteTestimonial',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.delete(API_ENDPOINTS.TESTIMONIAL(id));
            const { status, message } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Testimonial deleted', id };
            } else {
                return rejectWithValue({ message: message || 'Failed to delete testimonial', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete testimonial';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const toggleTestimonialStatus = createAsyncThunk(
    'testimonials/toggleStatus',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.patch(API_ENDPOINTS.TESTIMONIAL_TOGGLE(id));
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Status toggled', id, is_active: data?.is_active };
            } else {
                return rejectWithValue({ message: message || 'Failed to toggle status', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to toggle status';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const exportTestimonials = createAsyncThunk(
    'testimonials/export',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.TESTIMONIALS_EXPORT, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Exported successfully', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to export', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to export';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    items: [],
    pagination: { current_page: 1, last_page: 1, per_page: 15, total: 0 },
    currentItem: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const testimonialSlice = createSlice({
    name: 'testimonials',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        clearSuccessMessage: (state) => { state.successMessage = null; },
        clearApiMessage: (state) => { state.apiMessage = null; },
        clearState: (state) => {
            state.items = [];
            state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            state.currentItem = null;
            state.isLoading = false;
            state.isSubmitting = false;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
        },
        clearCurrentItem: (state) => { state.currentItem = null; },
    },
    extraReducers: (builder) => {
        builder
            // GET ITEMS
            .addCase(getTestimonials.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getTestimonials.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload;
                const responseData = payload.data || {};
                state.items = responseData.data || [];
                state.pagination = responseData.meta || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
                state.successMessage = payload.message || 'Testimonials retrieved';
                state.apiMessage = payload.message || 'Testimonials retrieved';
                state.error = null;
            })
            .addCase(getTestimonials.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch testimonials';
                state.apiMessage = action.payload?.message || 'Failed to fetch testimonials';
                state.successMessage = null;
                state.items = [];
                state.pagination = { current_page: 1, last_page: 1, per_page: 15, total: 0 };
            })
            // GET ITEM
            .addCase(getTestimonial.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getTestimonial.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentItem = action.payload.testimonial || null;
                state.successMessage = action.payload.message || 'Testimonial retrieved';
                state.apiMessage = action.payload.message || 'Testimonial retrieved';
                state.error = null;
            })
            .addCase(getTestimonial.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch testimonial';
                state.apiMessage = action.payload?.message || 'Failed to fetch testimonial';
                state.successMessage = null;
                state.currentItem = null;
            })
            // CREATE
            .addCase(createTestimonial.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createTestimonial.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const newItem = action.payload.testimonial;
                if (newItem) {
                    state.items = [newItem, ...state.items];
                }
                state.successMessage = action.payload.message || 'Testimonial created';
                state.apiMessage = action.payload.message || 'Testimonial created';
                state.error = null;
            })
            .addCase(createTestimonial.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create testimonial';
                state.apiMessage = action.payload?.message || 'Failed to create testimonial';
                state.successMessage = null;
            })
            // UPDATE
            .addCase(updateTestimonial.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateTestimonial.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const updated = action.payload.testimonial;
                if (updated) {
                    state.items = state.items.map(item => item.id === updated.id ? updated : item);
                    if (state.currentItem?.id === updated.id) {
                        state.currentItem = updated;
                    }
                }
                state.successMessage = action.payload.message || 'Testimonial updated';
                state.apiMessage = action.payload.message || 'Testimonial updated';
                state.error = null;
            })
            .addCase(updateTestimonial.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update testimonial';
                state.apiMessage = action.payload?.message || 'Failed to update testimonial';
                state.successMessage = null;
            })
            // DELETE
            .addCase(deleteTestimonial.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteTestimonial.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const id = action.payload.id;
                state.items = state.items.filter(item => item.id !== id);
                if (state.currentItem?.id === id) state.currentItem = null;
                state.successMessage = action.payload.message || 'Testimonial deleted';
                state.apiMessage = action.payload.message || 'Testimonial deleted';
                state.error = null;
            })
            .addCase(deleteTestimonial.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete testimonial';
                state.apiMessage = action.payload?.message || 'Failed to delete testimonial';
                state.successMessage = null;
            })
            // TOGGLE
            .addCase(toggleTestimonialStatus.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(toggleTestimonialStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                const { id, is_active } = action.payload;
                state.items = state.items.map(item => item.id === id ? { ...item, is_active } : item);
                if (state.currentItem?.id === id) state.currentItem = { ...state.currentItem, is_active };
                state.successMessage = action.payload.message || 'Status toggled';
                state.apiMessage = action.payload.message || 'Status toggled';
                state.error = null;
            })
            .addCase(toggleTestimonialStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to toggle status';
                state.apiMessage = action.payload?.message || 'Failed to toggle status';
                state.successMessage = null;
            })
            // EXPORT
            .addCase(exportTestimonials.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportTestimonials.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message || 'Exported successfully';
                state.apiMessage = action.payload.message || 'Exported successfully';
                state.error = null;
            })
            .addCase(exportTestimonials.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export';
                state.apiMessage = action.payload?.message || 'Failed to export';
                state.successMessage = null;
            });
    }
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearState,
    clearCurrentItem,
} = testimonialSlice.actions;

export const selectTestimonials = (state) => state.testimonials?.items || [];
export const selectTestimonialPagination = (state) => state.testimonials?.pagination || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
export const selectCurrentTestimonial = (state) => state.testimonials?.currentItem || null;
export const selectTestimonialsLoading = (state) => state.testimonials?.isLoading || false;
export const selectTestimonialsSubmitting = (state) => state.testimonials?.isSubmitting || false;
export const selectTestimonialsError = (state) => state.testimonials?.error || null;
export const selectTestimonialsApiMessage = (state) => state.testimonials?.apiMessage || null;

export default testimonialSlice.reducer;